import React, { useState, useRef, useEffect } from "react";
import { CloudUpload, FileText, CheckCircle, ChevronDown, Download } from "lucide-react";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { ReactComponent as Eye } from "../../../../icons/View_eye.svg";
import { ReactComponent as Delete } from "../../../../icons/View_dlt.svg";
import { GridcoTitle } from "../../components";
import { TabSwitcher } from "../../Gridco_Das";
import Hamburger from "../../../../components/Hamburger";
import { ErrorPopup } from "../../../../components/ErrorHandling";
import { uploadGridcoDcFile, uploadGridcoScheduleFile, getGridcoModReportExcel } from "../../../../Rest_api/RestApi";
import { useSelector } from "react-redux";
import { BASE_URL } from "../../../../configs";
import { getAccessToken } from "../../../Protected";

type UploadedFile = {
    type: string;
    file: File;
    id: number;
    isTemplate?: boolean;
};

type FileRowProps = {
    fileName: string;
    uploadedFile: File | null;
    fileError: string | null;
    onFileChange: (fileName: string, file: File | null) => void;
    onFileError: (fileName: string, error: string | null) => void;
    onDownloadTemplate: (fileName: string) => void;
};

const FileRow: React.FC<FileRowProps> = ({
    fileName,
    uploadedFile,
    fileError,
    onFileChange,
    onFileError,
    onDownloadTemplate,
}) => {
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleUploadClick = () => {
        fileInputRef.current?.click();
    };

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            const fileExtension = file.name.split(".").pop()?.toLowerCase();
            if (
                fileExtension !== "xlsx" &&
                fileExtension !== "csv" &&
                fileExtension !== "pdf"
            ) {
                onFileError(
                    fileName,
                    "Invalid file type. Only .xlsx, .csv and .pdf files are allowed."
                );
                onFileChange(fileName, null);
                return;
            }
            onFileError(fileName, null);
            onFileChange(fileName, file);
        } else {
            onFileChange(fileName, null);
            onFileError(fileName, null);
        }
    };

    const handleViewFile = () => {
        if (uploadedFile) {
            const url = URL.createObjectURL(uploadedFile);
            window.open(url, "_blank");
        }
    };

    const handleDeleteFile = () => {
        onFileChange(fileName, null);
        onFileError(fileName, null);
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    const isReady = uploadedFile && !fileError;
    const isError = !uploadedFile && fileError;

    return (
        <div className="flex items-center justify-between py-2 px-1 border mb-3 border-[#F3F5F8] last:border-b-0">
            <div className="flex items-center gap-3">
                <FileText size={20} style={{ color: "#8C8A96" }} />
                <span className="text-sm font-medium text-[#525062]">{fileName}</span>
            </div>

            {isReady ? (
                <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                        <button
                            onClick={handleViewFile}
                            className="text-[#F1935C] hover:opacity-80 transition-opacity p-1 rounded"
                        >
                            <Eye />
                        </button>
                        <button
                            className="flex items-center justify-center gap-2 w-[80px] h-[33px] text-xs font-medium rounded-full"
                            style={{ backgroundColor: "#00BF6F", color: "#fff" }}
                        >
                            <CheckCircle size={14} /> <span>Ready</span>
                        </button>
                        <button
                            onClick={handleDeleteFile}
                            className="text-[#6C757D] hover:text-red-500 transition-colors p-1 rounded"
                        >
                            <Delete className="w-8 h-8" />
                        </button>
                    </div>
                </div>
            ) : isError ? (
                <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                        <button
                            className="flex items-center justify-center gap-2 w-[80px] h-[33px] text-xs font-medium rounded-full"
                            style={{ backgroundColor: "#FDE6E8", color: "#B32130" }}
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="14"
                                height="14"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            >
                                <circle cx="12" cy="12" r="10" />
                                <path d="m15 9-6 6" />
                                <path d="m9 9 6 6" />
                            </svg>{" "}
                            <span>Error</span>
                        </button>
                        <button
                            onClick={handleDeleteFile}
                            className="text-[#6C757D] hover:text-red-500 transition-colors p-1 rounded"
                        >
                            <Delete className="w-8 h-8" />
                        </button>
                    </div>
                </div>
            ) : (
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => onDownloadTemplate(fileName)}
                        className="flex items-center justify-center gap-2 w-[110px] h-[30px] text-xs rounded-lg border-[0.5px] border-[#F1935C] font-medium transition-colors bg-[#FFFAF6] text-[#F1935C]"
                        style={{ border: "0.5px solid #F1935C" }}
                    >
                        <Download className="w-4 h-4" />
                        <span>Template</span>
                    </button>
                    <button
                        onClick={handleUploadClick}
                        className="flex items-center justify-center gap-2 w-[90px] h-[30px] text-xs rounded-lg border-[0.5px] border-[#34656D] font-medium transition-colors"
                        style={{ backgroundColor: "#EFFCFF", color: "#34656D" }}
                    >
                        <CloudUpload size={14} /> Upload
                    </button>
                </div>
            )}
            <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                className="hidden"
                accept=".xlsx,.csv,.pdf"
            />
        </div>
    );
};

const FileRegexPatterns: { [key: string]: RegExp } = {
    AVT: /^AVT \d{2}\.\d{2}\.\d{4}\s*\.xlsx(?: - .*\.csv)?$/i,
    "Day Ahead Schedule for MTOA": /^Day Ahead Schedule for MTOA.*\(.*\).*\.xlsx$/i,
    MAADURGA_DIRECT: /^Maa\s*Durga.*\.xlsx$/i,
    "Declared Capacity of GKEL": /^Declared Capacity of GKEL for\s+\d{2}\.\d{2}\.\d{4}\s*\.xlsx$/i,
    "IEX DOR GRIDCO": /^IEX\d{6}DOR_GDC0001_OR0_GRIDCO\.xls$/i,
    "JSW Energy Utkal Schedule": /^JSW Energy Utkal Schedule & GRIDCO allocation for \d{2}\.\d{2}\.\d{2}\.xlsx$/i,
    "NetSchdReportBreakup ISGS": /^NetSchdReportBreakup@ODISHA_STATE@ISGS@rev\(\d+\)@\d{2}-\d{2}-\d{4}@\d{2}-\d{2}-\d{4}_\d{2}-\d{2}-\d{2}\.xlsx$/i,
    "NetSchdReportBreakup OA_GNA": /^NetSchdReportBreakup@ODISHA_STATE@OA_GNA@rev\(\d+\)@\d{2}-\d{2}-\d{4}@\d{2}-\d{2}-\d{4}_\d{2}-\d{2}-\d{2}\.xlsx$/i,
    "NetSchdReportBreakup OA_REMC": /^NetSchdReportBreakup@ODISHA_STATE@OA_REMC@rev\(\d+\)@\d{2}-\d{2}-\d{4}@\d{2}-\d{2}-\d{4}_\d{2}-\d{2}-\d{2}\.xlsx$/i,
    "OPGC STAGE-2 DC": /^OPGC STAGE-2 DC\s+\d{2}\.\d{2}\.\d{4}\s+-\s+R\d+\.xlsx$/i,
    "RTM_IEX DOR GRIDCO": /^RTM_IEX\d{6}DOR_GDC0001_OR0_GRIDCO(\s?\(1\))?\.XLS$/i,
    "SLDC LDS": /^SLDC LDS \d{2} (Jan(?:uary)?|Feb(?:ruary)?|Mar(?:ch)?|Apr(?:il)?|May|Jun(?:e)?|Jul(?:y)?|Aug(?:ust)?|Sep(?:tember)?|Oct(?:ober)?|Nov(?:ember)?|Dec(?:ember)?) \d{4} \(Rev-\d+\)\.xlsx$/i,
    "NBVL(IPP)": /^NBVLIPP_DAY_AHEAD_SCHEDULE_TO_GRIDCO_FOR_(\d{2}\.\d{2}\.\d{4})\.xlsx$/i,
    MAADURGA: /^DC_INJECTION_\d{2}\.\d{2}\.\d{4}\.xlsx$/i,
    jipl_dc_pdf: /^JIPL DC for DD \d{2}\.\d{2}\.\d{4}\.pdf$/i,
    sldc_report: /^SLDC_Report__(\d{2})(\d{2})(\d{4})\.xlsx(?: - .*\.csv)?$/i,
    "VC Generator": /^VC_all_generators\.xlsx$/i,
    Intra_State_RE: /^Intra_State_RE\.xlsx$/i,
    CGP_Injection: /^CGP_Injection\.xlsx$/i,
    Buffer: /^Buffer\.xlsx$/i,
    Banking: /^Banking.*\.xlsx$/i,
    "Short-Term": /^Short[ -]Term.*\.xlsx$/i,
    Demand: /^Actual Demand DD (\d{2})\.(\d{2})\.(\d{4}).*\.xlsx$/i,
    DAM_XLSX: /^IEX(\d{6})DOR_GDC0001_OR0_GRIDCO.*\.xlsx$/i,
    TAM: /^TAM\.(xlsx|xls|csv|pdf)$/i,
    DAM_TEMPLATE: /^DAM\.xlsx$/i,
    TAM_TEMPLATE: /^TAM\.xlsx$/i,
    RTM_TEMPLATE: /^RTM\.xlsx$/i,
    DEMAND_TEMPLATE: /^Demand\.xlsx$/i,
    JIPL_TEMPLATE: /^JIPL\.xlsx$/i,
    JSWEUL_TEMPLATE: /^JSWEUL\.xlsx$/i,
    "Maa Durga_TEMPLATE": /^Maa Durga\.xlsx$/i,
    NBVL_TEMPLATE: /^NBVL\.xlsx$/i,
    "NET SCHEDULED_TEMPLATE": /^NET SCHEDULED\.xlsx$/i,
    OA_GNA_TEMPLATE: /^OA_GNA\.xlsx$/i,
    OA_REMC_TEMPLATE: /^OA_REMC\.xlsx$/i,
    "OPGC-II Unit-3&4_TEMPLATE": /^OPGC-II Unit-3&4\.xlsx$/i,
    "STATE HYDRO_TEMPLATE": /^STATE HYDRO\.xlsx$/i,
    Vedanta_TEMPLATE: /^Vedanta\.xlsx$/i,
    GMR_TEMPLATE: /^GMR\.xlsx$/i,
    IBTPS_TEMPLATE: /^IBTPS\.xlsx$/i,
    // TPA_SOLAR: /^TPA SOLAR.*\.xlsx$/i,
    // TPA: /^TPA(?!\s*SOLAR).*\.xlsx$/i,
};

const RegexToFileTypeMap: { [key: string]: string } = {
    AVT: "OPGC-1 (IBTPS Unit -1 & 2)",
    "OPGC STAGE-2 DC": "OPGC-2 (IBTPS Unit -3 & 4)",
    "SLDC LDS": "Vedanta",
    "Declared Capacity of GKEL": "GMR",
    "JSW Energy Utkal Schedule": "JSWEUL",
    "NBVL(IPP)": "NBVL",
    "NetSchdReportBreakup ISGS": "Net Schedule ISGS",
    "NetSchdReportBreakup OA_GNA": "Net Schedule OA GNA",
    "NetSchdReportBreakup OA_REMC": "Net Schedule OA REMC",
    "RTM_IEX DOR GRIDCO": "RTM",
    "IEX DOR GRIDCO": "IEX",
    "Day Ahead Schedule for MTOA": "Maa Durga",
    MAADURGA_DIRECT: "Maa Durga",
    MAADURGA: "Maa Durga",
    jipl_dc_pdf: "JIPL",
    sldc_report: "State Hydro Generation",
    "VC Generator": "VC for Generators",
    Intra_State_RE: "Intra State RE",
    CGP_Injection: "CGP Injection",
    Buffer: "Buffer",
    Banking: "Banking",
    "Short-Term": "Short Term Buy/Sell",
    Demand: "Demand",
    DAM_XLSX: "DAM",
    TAM: "TAM",
    DAM_TEMPLATE: "DAM Template",
    TAM_TEMPLATE: "TAM Template",
    RTM_TEMPLATE: "RTM Template",
    DEMAND_TEMPLATE: "Demand Template",
    JIPL_TEMPLATE: "JIPL Template",
    JSWEUL_TEMPLATE: "JSWEUL Template",
    "Maa Durga_TEMPLATE": "Maa Durga Template",
    NBVL_TEMPLATE: "NBVL Template",
    "NET SCHEDULED_TEMPLATE": "Net Schedule ISGS Template",
    OA_GNA_TEMPLATE: "Net Schedule OA GNA Template",
    OA_REMC_TEMPLATE: "Net Schedule OA REMC Template",
    "OPGC-II Unit-3&4_TEMPLATE": "OPGC-2 (IBTPS Unit -3 & 4) Template",
    "STATE HYDRO_TEMPLATE": "State Hydro Generation Template",
    Vedanta_TEMPLATE: "Vedanta Template",
    GMR_TEMPLATE: "GMR Template",
    IBTPS_TEMPLATE: "OPGC-1 (IBTPS Unit -1 & 2) Template",
    // TPA_SOLAR: "TPA Solar",
    // TPA: "TPA",
};

const TemplateToStandardMap: { [key: string]: string } = {
    "JIPL Template": "JIPL",
    "JSWEUL Template": "JSWEUL",
    "Maa Durga Template": "Maa Durga",
    "NBVL Template": "NBVL",
    "Net Schedule ISGS Template": "Net Schedule ISGS",
    "Net Schedule OA GNA Template": "Net Schedule OA GNA",
    "Net Schedule OA REMC Template": "Net Schedule OA REMC",
    "OPGC-2 (IBTPS Unit -3 & 4) Template": "OPGC-2 (IBTPS Unit -3 & 4)",
    "State Hydro Generation Template": "State Hydro Generation",
    "Vedanta Template": "Vedanta",
    "GMR Template": "GMR",
    "OPGC-1 (IBTPS Unit -1 & 2) Template": "OPGC-1 (IBTPS Unit -1 & 2)",
    "DAM Template": "DAM",
    "TAM Template": "TAM",
    "RTM Template": "RTM",
    "Demand Template": "Demand",
};

const getValidPatternsForFileType = (fileName: string): RegExp[] => {
    const patterns: RegExp[] = [];

    // Add standard patterns
    Object.entries(RegexToFileTypeMap).forEach(([regexKey, mappedType]) => {
        if (mappedType === fileName) {
            const pattern = FileRegexPatterns[regexKey];
            if (pattern) {
                patterns.push(pattern);
            }
        }
    });

    // Add template patterns that map to this standard fileName
    const templateTypes = Object.entries(TemplateToStandardMap)
        .filter(([temp, std]) => std === fileName)
        .map(([temp]) => temp);

    templateTypes.forEach((tempType) => {
        Object.entries(RegexToFileTypeMap).forEach(([regexKey, mappedType]) => {
            if (mappedType === tempType) {
                const pattern = FileRegexPatterns[regexKey];
                if (pattern) {
                    patterns.push(pattern);
                }
            }
        });
    });

    return patterns;
};

const detectFileType = (fileName: string, currentTabTypes: string[]): { type: string, isTemplate: boolean } => {
    for (const [regexKey, pattern] of Object.entries(FileRegexPatterns)) {
        if (pattern.test(fileName)) {
            const mappedType = RegexToFileTypeMap[regexKey];
            if (mappedType) {
                const isTemplate = mappedType.includes("Template");
                const standardType = isTemplate ? TemplateToStandardMap[mappedType] : mappedType;
                
                if (standardType && currentTabTypes.includes(standardType)) {
                    return { type: mappedType, isTemplate };
                }
            }
        }
    }
    return { type: "", isTemplate: false };
};

// ── File types required for DAM Performance Analysis ──
const FILE_TYPES = [
    "VC for Generators",
    "Net Schedule ISGS",
    "State Hydro and OPGC-1 (IBTPS Unit -1 & 2)",
    "OPGC-2 (IBTPS Unit -3 & 4)",
    "Vedanta",
    "GMR",
    "Net Schedule OA REMC",
    "Net Schedule OA GNA",
    "OA Gridco",
    "DAM",
    "RTM",
    "TAM",
];

// ── Backend alias map for Schedule file types ──
const Schedule_FileTypeBackendAlias: { [key: string]: string } = {
    "VC for Generators": "vc-generator",
    "Net Schedule ISGS": "isgs",
    "State Hydro and OPGC-1 (IBTPS Unit -1 & 2)": "opgc-1",
    "OPGC-2 (IBTPS Unit -3 & 4)": "opgc-2",
    "Vedanta": "vedanta",
    "GMR": "gmr",
    "Net Schedule OA REMC": "oa-remc",
    "Net Schedule OA GNA": "oa-gna",
    "OA Gridco": "oa-gridco",
    "DAM": "dam",
    "RTM": "rtm",
    "TAM": "tam",
    "Net Schedule ISGS Template": "isgs-temp",
    "State Hydro and OPGC-1 (IBTPS Unit -1 & 2) Template": "opgc-1-temp",
    "OPGC-2 (IBTPS Unit -3 & 4) Template": "opgc-2-temp",
    "Vedanta Template": "vedanta-temp",
    "GMR Template": "gmr-temp",
    "Net Schedule OA REMC Template": "oa-remc-temp",
    "Net Schedule OA GNA Template": "oa-gna-temp",
};

// ── File types required for DC (Declared Capacity) ──
const DC_FILE_TYPES = [
    "VC for Generators",
    "Net Schedule ISGS",
    "OPGC-1 (IBTPS Unit -1 & 2)",
    "OPGC-2 (IBTPS Unit -3 & 4)",
    "Vedanta",
    "GMR",
    "JIPL",
    "JSWEUL",
    "NBVL",
    "Maa Durga",
    "Net Schedule OA REMC",
    "Net Schedule OA GNA",
    "State Hydro Generation",
    "Intra State RE",
    "CGP Injection",
    "Buffer",
    "Banking",
    "Short Term Buy/Sell",
    "Demand",
    // "TPA",
    // "TPA Solar",
    "DAM",
    "TAM",
    "RTM",
];

// ── Backend alias map for DC file types ──
const DC_FileTypeBackendAlias: { [key: string]: string } = {
    "VC for Generators": "vc-generator",
    "Net Schedule ISGS": "isgs",
    "OPGC-1 (IBTPS Unit -1 & 2)": "opgc-1",
    "OPGC-2 (IBTPS Unit -3 & 4)": "opgc-2",
    "Vedanta": "vedanta",
    "GMR": "gmr",
    "JIPL": "jipl",
    "JSWEUL": "jsweul",
    "NBVL": "nbvl",
    "Maa Durga": "maa-durga",
    "Net Schedule OA REMC": "oa-remc",
    "Net Schedule OA GNA": "oa-gna",
    "State Hydro Generation": "state-hydro",
    "Intra State RE": "state-re",
    "CGP Injection": "cgp",
    "Buffer": "buffer",
    "Banking": "banking",
    "Short Term Buy/Sell": "short-term-buy-sell",
    "Demand": "demand",
    "DAM": "dam",
    "TAM": "tam",
    "RTM": "rtm",
    "Demand Template": "demand",
    "DAM Template": "dam-temp",
    "TAM Template": "tam-temp",
    "RTM Template": "rtm-temp",
    "Net Schedule ISGS Template": "isgs-temp",
    "OPGC-1 (IBTPS Unit -1 & 2) Template": "opgc-1-temp",
    "OPGC-2 (IBTPS Unit -3 & 4) Template": "opgc-2-temp",
    "Vedanta Template": "vedanta-temp",
    "GMR Template": "gmr-temp",
    "Net Schedule OA REMC Template": "oa-remc-temp",
    "Net Schedule OA GNA Template": "oa-gna-temp",
    "Maa Durga Template": "maa-durga-temp",
    "JIPL Template": "jipl-temp",
    "JSWEUL Template": "jsweul-temp",
    "NBVL Template": "nbvl-temp",
    "State Hydro Generation Template": "state-hydro-temp",
    // "TPA": "tpa",
    // "TPA Solar": "tpa-solar",
};

// ── Upload mode tabs ──
const UPLOAD_TABS = ["DC"] as const; // Only DC for now, Schedule commented out
// const UPLOAD_TABS = ["DC", "Schedule"] as const;
type UploadTab = (typeof UPLOAD_TABS)[number] | "Schedule";

// ── Simple header title ──
function UploadTitle() {
    return (
        <div className="flex items-center gap-2 p-1 px-4 bg-[#F3F5F8] rounded-xl">
            <span className="text-sm font-semibold text-[#34656D]">Upload Data (DC)</span>
        </div>
    );
}

// ── Tab switcher component (Disabled for now) ──
function UploadTabSwitcher({
    activeTab,
    dcCount,
    dcTotal,
}: {
    activeTab: string;
    dcCount: number;
    dcTotal: number;
}) {
    return (
        <div className="flex items-center gap-1 p-1 rounded-xl bg-[#F3F5F8]">
            <button
                className="flex items-center gap-2 px-4 py-1.5 text-sm font-medium rounded-lg bg-white text-[#34656D] shadow-sm"
            >
                {activeTab}
                <span className="text-xs px-1.5 py-0.5 rounded-full bg-[#34656D] text-white">
                    {dcCount}/{dcTotal}
                </span>
            </button>
        </div>
    );
}

// ── Custom Select Component for Height Control ──
function CustomSelect({
    options,
    value,
    onChange,
    placeholder = "Select File",
}: {
    options: string[];
    value: string;
    onChange: (val: string) => void;
    placeholder?: string;
}) {
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    return (
        <div className="relative inline-block w-[300px]" ref={containerRef}>
            <div
                onClick={() => setIsOpen(!isOpen)}
                className="flex items-center justify-between border border-[#C5C5CBD9] rounded-lg p-2 bg-white cursor-pointer text-sm text-[#8C8A96]"
            >
                <span className="truncate">{value || placeholder}</span>
                <ChevronDown size={16} className={`transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`} />
            </div>

            {isOpen && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-[#C5C5CBD9] rounded-lg shadow-xl z-[100] overflow-hidden">
                    <div className="max-height-[250px] overflow-y-auto" style={{ maxHeight: '250px' }}>
                        <div
                            key="all-files"
                            className={`px-4 py-2 text-sm cursor-pointer hover:bg-[#F3F5F8] ${value === "All Files" ? "bg-[#EFFCFF] text-[#34656D] font-medium" : "text-[#8C8A96]"}`}
                            onClick={() => {
                                onChange("All Files");
                                setIsOpen(false);
                            }}
                        >
                            All Files
                        </div>
                        {options.map((option, idx) => (
                            <div
                                key={idx}
                                className={`px-4 py-2 text-sm cursor-pointer hover:bg-[#F3F5F8] ${value === option ? "bg-[#EFFCFF] text-[#34656D] font-medium" : "text-[#8C8A96]"}`}
                                onClick={() => {
                                    onChange(option);
                                    setIsOpen(false);
                                }}
                            >
                                {option}
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

const DC_FILE_CONFIG: Record<string, string[]> = {
    "VC for Generators": [],
    "OPGC-2 (IBTPS Unit -3 & 4)": ["OPGC-II Unit-3&4"],
    "OPGC-1 (IBTPS Unit -1 & 2)": ["IBTPS"],
    "Vedanta": ["Vedanta"],
    "GMR": ["GMR"],
    "JIPL": ["JIPL"],
    "JSWEUL": ["JSWEUL"],
    "NBVL": ["NBVL"],
    "Maa Durga": ["Maa Durga"],
    "Net Schedule OA REMC": [
        "AP41PL_BHDL", "AP43PL_BKN", "AWEK1L", "DADRI_SOLAR", "GADAG_GreenInfra_W",
        "GIWEL_SECI_II_RE", "GSECL_SOLAR", "OKWPL_RE", "RWE_AP2_SECI_III", "Tuticorin_BETAMWIND",
        "GPCL_SOLAR_10", "IRON_TRINGLE_10_MW_SOLAR", "XXPPL_FTG3", "ENERSON_SOLAR_10MW"
    ],
    "Net Schedule OA GNA": ["FARIDABAD_SOLAR", "RAJ_SOLAR", "RAJ_SOLAR"],
    "State Hydro Generation": [
        "Burla", "Chiplima", "Balimela", "Rengali", "U Kolab", "U Indravati", "Machkund O/D"
    ],
    "Net Schedule ISGS": [
        "BARH|TO_ODISHA", "BARH_I|TO_ODISHA", "CHUKHA|TO_ODISHA", "Darlipali_NTPC|TO_ODISHA",
        "FSTPP_III|TO_ODISHA", "FSTPP_III|TO_ODISHA_Solar_Beneficiary", "FSTPP_I_II|TO_ODISHA",
        "FSTPP_I_II|TO_ODISHA_Solar_Beneficiary", "KGSU1AND2|TO_ODISHA", "KGSU3AND4|TO_ODISHA",
        "KHSTPP_I|TO_ODISHA", "KHSTPP_I|TO_ODISHA_Solar_Beneficiary", "KHSTPP_II|TO_ODISHA",
        "KHSTPP_II|TO_ODISHA_Solar_Beneficiary", "KKNP|TO_ODISHA", "KKNPPU2|TO_ODISHA",
        "KUDGI|TO_ODISHA", "KURICHU|TO_ODISHA", "MANGDECHU|TO_ODISHA", "MAPS|TO_ODISHA",
        "MTPS_II|TO_ODISHA", "NLCEXP|TO_ODISHA", "NLCIIST1|TO_ODISHA", "NLCIIST2|TO_ODISHA",
        "NLCTS2EXP|TO_ODISHA", "NNTPP|TO_ODISHA", "NPGC|TO_ODISHA", "NTPL|TO_ODISHA",
        "North_Karanpura_STPS|TO_ODISHA", "RANGIT|TO_ODISHA", "RSTPSU1TO6|TO_ODISHA",
        "RSTPSU7|TO_ODISHA", "SIMHST1|TO_ODISHA", "SIMHST2|TO_ODISHA", "TALA|TO_ODISHA",
        "TALST2|TO_ODISHA", "TSTPP_I|TO_ODISHA", "TSTPP_I|TO_ODISHA_Solar_Beneficiary",
        "Teesta_V|TO_ODISHA", "TelanganaSTPP|TO_ODISHA", "VALLURNTECL|TO_ODISHA"
    ],
    "Intra State RE": ["SOLAR", "WIND", "BIOMASS", "SHEP"],
    "CGP Injection": ["CGP_INJECTION"],
    "Buffer": ["buffer"],
    "Banking": ["Total"],
    "Short Term Buy/Sell": ["Total"],
    "Demand": ["Demand"],
    // "TPA": ["quantum", "rate"],
    // "TPA Solar": ["quantum", "rate"],
    "DAM": ["quantum", "price"],
    "TAM": ["quantum", "price"],
    "RTM": ["quantum", "price"],
};

function getCustomFileName(fileName: string) {
    switch (fileName) {
        case "VC for Generators":
            return `VC_all_generators.xlsx`;
        case "Net Schedule ISGS":
            return `NET SCHEDULED.xlsx`;
        case "OPGC-2 (IBTPS Unit -3 & 4)":
            return `OPGC-II Unit-3&4.xlsx`;
        case "OPGC-1 (IBTPS Unit -1 & 2)":
            return `IBTPS.xlsx`;
        case "Net Schedule OA GNA":
            return `OA_GNA.xlsx`;
        case "Net Schedule OA REMC":
            return `OA_REMC.xlsx`;
        case "GMR":
            return `GMR.xlsx`;
        case "JIPL":
            return `JIPL.xlsx`;
        case "JSWEUL":
            return `JSWEUL.xlsx`;
        case "NBVL":
            return `NBVL.xlsx`;
        case "Maa Durga":
            return `Maa Durga.xlsx`;
        case "State Hydro Generation":
            return `STATE HYDRO.xlsx`;
        case "Intra State RE":
            return `Intra_State_RE.xlsx`;
        case "CGP Injection":
            return `CGP_Injection.xlsx`;
        case "Buffer":
            return `Buffer.xlsx`;
        case "Short Term Buy/Sell":
            return `Short-Term.xlsx`;
        case "Demand":
            return `Demand.xlsx`;
        // case "TPA":
        //     return `TPA.xlsx`;
        // case "TPA Solar":
        //     return `TPA SOLAR.xlsx`;
        case "DAM":
            return `DAM.xlsx`;
        case "TAM":
            return `TAM.xlsx`;
        case "RTM":
            return `RTM.xlsx`;
        default:
            return `${fileName}.xlsx`;
    }
}

export default function DamPerformanceUpload() {
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const activeUploadTab = "DC";
    const [selectedDate, setSelectedDate] = useState<string>("");

    const [dcSubmitted, setDcSubmitted] = useState(false);

    // ── Schedule state ──
    const [scheduleFiles, setScheduleFiles] = useState<UploadedFile[]>([]);
    const [scheduleFileErrors, setScheduleFileErrors] = useState<Record<string, string | null>>({});
    const [scheduleSelectedFileType, setScheduleSelectedFileType] = useState<string>("All Files");

    // ── DC state ──
    const [dcFiles, setDcFiles] = useState<UploadedFile[]>([]);
    const [dcFileErrors, setDcFileErrors] = useState<Record<string, string | null>>({});
    const [dcSelectedFileType, setDcSelectedFileType] = useState<string>("All Files");

    const preSelectedDate = useSelector(
        (state: any) => state.modGridcoDate.selectedDate
    );
    const modSelectedSession = useSelector(
        (state: any) => state.modGridcoSession.modSession
    );

    const scheduleBulkRef = useRef<HTMLInputElement>(null);
    const dcBulkRef = useRef<HTMLInputElement>(null);

    // ── Helpers to get/set state based on active tab ──
    // ── Helpers to get/set state based on active tab ──
    const isSchedule = false; // Always false as only DC tab is active
    const currentFileTypes = DC_FILE_TYPES; // Always DC_FILE_TYPES
    const currentFiles = dcFiles; // Always dcFiles
    const setCurrentFiles = setDcFiles; // Always setDcFiles
    const currentFileErrors = dcFileErrors; // Always dcFileErrors
    const setCurrentFileErrors = setDcFileErrors; // Always setDcFileErrors
    const currentSelectedFileType = dcSelectedFileType; // Always dcSelectedFileType
    const setCurrentSelectedFileType = setDcSelectedFileType; // Always setDcSelectedFileType
    const currentBulkRef = dcBulkRef; // Always dcBulkRef
    
    const handleBulkUploadClick = () => {
        currentBulkRef.current?.click();
    };

    const handleBulkUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (!selectedDate) {
            setError("Please select a date first");
            event.target.value = "";
            return;
        }

        if (!files || files.length === 0) {
            setError("Please select files");
            return;
        }

        const validExtensions = [".xlsx", ".xls", ".csv", ".pdf"];
        const newDetectedFiles: UploadedFile[] = [];
        const undetectedFiles: string[] = [];

        for (let i = 0; i < files.length; i++) {
            const file = files[i];
            const fileExtension = file.name
                .substring(file.name.lastIndexOf("."))
                .toLowerCase();

            if (!validExtensions.includes(fileExtension)) {
                setError(
                    `Invalid file format for ${file.name}. Please select only XLSX, XLS, CSV or PDF files.`
                );
                event.target.value = "";
                return;
            }

            const { type: detectedType, isTemplate } = detectFileType(file.name, currentFileTypes);
            
            if (!detectedType) {
                undetectedFiles.push(file.name);
                continue;
            }

            newDetectedFiles.push({
                type: detectedType,
                file: file,
                id: Date.now() + i,
                isTemplate: isTemplate,
            });
        }

        if (undetectedFiles.length > 0) {
            setError(
                `Could not auto-detect or match file type for the current tab:\n${undetectedFiles.join(
                    "\n"
                )}\n\nPlease upload these files individually or check if you are on the correct tab.`
            );
        }

        if (newDetectedFiles.length > 0) {
            setCurrentFiles((prev: UploadedFile[]) => {
                const affectedStandardTypes = new Set(newDetectedFiles.map((f: UploadedFile) => TemplateToStandardMap[f.type] || f.type));
                const kept = prev.filter((f: UploadedFile) => {
                    const fStandard = TemplateToStandardMap[f.type] || f.type;
                    return !affectedStandardTypes.has(fStandard);
                });
                return [...kept, ...newDetectedFiles];
            });
            setCurrentFileErrors((prev: Record<string, string | null>) => {
                const updated = { ...prev };
                newDetectedFiles.forEach((f: UploadedFile) => {
                    const standard = TemplateToStandardMap[f.type] || f.type;
                    updated[standard] = null;
                });
                return updated;
            });
        }

        event.target.value = "";
    };

    const handleFileChange = (fileName: string, file: File | null) => {
        if (!file) {
            setCurrentFiles((prev: UploadedFile[]) => prev.filter((f: UploadedFile) => {
                const fStandard = TemplateToStandardMap[f.type] || f.type;
                return fStandard !== fileName;
            }));
            setCurrentFileErrors((prev: Record<string, string | null>) => ({ ...prev, [fileName]: null }));
            return;
        }

        const validPatterns = getValidPatternsForFileType(fileName);
        if (validPatterns.length > 0) {
            const isValid = validPatterns.some((pattern) => pattern.test(file.name));
            if (!isValid) {
                const errorMsg = `File name does not match required pattern for ${fileName}.`;
                handleFileError(fileName, errorMsg);
                setCurrentFiles((prev: UploadedFile[]) => prev.filter((f: UploadedFile) => f.type !== fileName));
                return;
            }
        }

        const { type: detectedType, isTemplate } = detectFileType(file.name, currentFileTypes);

        setCurrentFiles((prev: UploadedFile[]) => {
            const filtered = prev.filter((f: UploadedFile) => {
                const fStandard = TemplateToStandardMap[f.type] || f.type;
                return fStandard !== fileName;
            });
            return [
                ...filtered,
                {
                    type: detectedType || fileName,
                    file,
                    id: Date.now(),
                    isTemplate,
                },
            ];
        });
        setCurrentFileErrors((prev: Record<string, string | null>) => ({ ...prev, [fileName]: null }));
    };

    const handleFileError = (fileName: string, error: string | null) => {
        setCurrentFileErrors((prev: Record<string, string | null>) => ({ ...prev, [fileName]: error }));
    };
    
    const handleDownloadTemplate = async (fileName: string) => {
        if (!fileName) return;

        const columns = DC_FILE_CONFIG[fileName];
        if (!columns) {
            console.error("❌ Unknown fileName:", fileName);
            return;
        }

        const timeSlots = Array.from({ length: 96 }, (_, i) => i + 1);

        if (fileName === "Net Schedule ISGS") {
            const plants = columns.map((col) => {
                const parts = col.split("|");
                return { name: parts[0], sub: parts[1] || "" };
            });
            const headerRow = ["time_block", ...plants.map((p) => p.name)];
            const subHeaderRow = ["", ...plants.map((p) => p.sub)];
            const dataRows = timeSlots.map((slot) => [slot, ...plants.map(() => "")]);

            const worksheetData = [headerRow, subHeaderRow, ...dataRows];
            const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);

            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, "Schedule");

            const excelBuffer = XLSX.write(workbook, {
                bookType: "xlsx",
                type: "array",
            });
            const blob = new Blob([excelBuffer], {
                type: "application/octet-stream",
            });
            saveAs(blob, getCustomFileName(fileName));
            return;
        } else if (fileName === "VC for Generators") {
            try {
                const url = `${BASE_URL}/mod_gridco/getGeneratorVC?date=${selectedDate}&session=${modSelectedSession}`;
                const response = await fetch(url, {
                    method: "GET",
                    headers: {
                        Authorization: `${getAccessToken() || ""}`,
                    },
                });

                if (!response.ok) {
                    throw new Error("Failed to fetch VC data");
                }

                const vcData = await response.json();
                if (!Array.isArray(vcData) || vcData.length === 0) {
                    throw new Error("No VC data available");
                }

                const headerRow = ["Generator Name", "VC (P/U)"];
                const dataRows = vcData.map((item) => [
                    item.generator || "",
                    item.vc || "",
                ]);

                const worksheetData = [headerRow, ...dataRows];
                const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
                const columnWidths = [{ wch: 30 }, { wch: 15 }];
                worksheet["!cols"] = columnWidths;

                const workbook = XLSX.utils.book_new();
                XLSX.utils.book_append_sheet(workbook, worksheet, "VC Data");

                const excelBuffer = XLSX.write(workbook, {
                    bookType: "xlsx",
                    type: "array",
                });
                const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
                saveAs(blob, getCustomFileName(fileName));
            } catch (error) {
                console.error("Error creating VC Generator Excel:", error);
                setError("Failed to create VC Generator Excel. Please try again later.");
            }
            return;
        }

        const data = timeSlots.map((slot) => {
            const row: Record<string, any> = { time_block: slot };
            columns.forEach((col) => {
                row[col] = "";
            });
            return row;
        });

        const worksheet = XLSX.utils.json_to_sheet(data, {
            header: ["time_block", ...columns],
        });
        XLSX.utils.sheet_add_aoa(worksheet, [["time_block", ...columns]], {
            origin: "A1",
        });

        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");

        const excelBuffer = XLSX.write(workbook, {
            bookType: "xlsx",
            type: "array",
        });
        const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
        saveAs(blob, getCustomFileName(fileName));
    };

    const filesToDisplay =
        currentSelectedFileType === "All Files"
            ? currentFileTypes
            : currentFileTypes.filter((fileType: string) => fileType === currentSelectedFileType);

    const scheduleUploadedCount = FILE_TYPES.filter((type: string) =>
        scheduleFiles.find((f: UploadedFile) => f.type === type)
    ).length;

    const dcUploadedCount = DC_FILE_TYPES.filter((type) =>
        dcFiles.find((f) => f.type === type || TemplateToStandardMap[f.type] === type)
    ).length;

    const getUploadedFileForStandardType = (standardFileName: string, files: UploadedFile[]) => {
        const standardFile = files.find((f) => f.type === standardFileName);
        if (standardFile) return standardFile;

        const templateFile = files.find((f) => TemplateToStandardMap[f.type] === standardFileName);
        return templateFile || null;
    };

    const allDcFilesUploaded = dcUploadedCount === DC_FILE_TYPES.length;
    const allScheduleFilesUploaded = scheduleUploadedCount === FILE_TYPES.length;

    const isDateSelected = selectedDate !== "";

    // ── Generate Report: uploads DC files ──
    const handleGenerateReportDC = async () => {
        if (!isDateSelected) {
            setError("Please select a date first.");
            return;
        }
        try {
            setLoading(true);
            const formData = new FormData();

            const metaData = dcFiles.map((file, idx) => {
                const field = `file_${idx}`;
                formData.append(field, file.file);
                return {
                    field,
                    date: selectedDate,
                    type: DC_FileTypeBackendAlias[file.type] || file.type,
                };
            });

            formData.append("metaData", JSON.stringify(metaData));
            formData.append("date", selectedDate);

            await uploadGridcoDcFile(formData);
            setDcSubmitted(true);
            setError(null);

            // Download the excel report
            const blob = await getGridcoModReportExcel(selectedDate);
            const downloadUrl = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = downloadUrl;
            link.download = `mod-report-${selectedDate}.xlsx`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(downloadUrl);
        } catch (err) {
            console.error("Error uploading DC files:", err);
            setError("Error uploading DC files. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    // ── Generate Report: uploads Schedule files ──
    const handleGenerateReport = async () => {
        if (!isDateSelected) {
            setError("Please select a date first.");
            return;
        }
        try {
            setLoading(true);
            const formData = new FormData();

            const metaData = scheduleFiles.map((file, idx) => {
                const field = `file_${idx}`;
                formData.append(field, file.file);
                return {
                    field,
                    date: selectedDate,
                    type: Schedule_FileTypeBackendAlias[file.type] || file.type,
                };
            });

            formData.append("metaData", JSON.stringify(metaData));
            formData.append("date", selectedDate);

            await uploadGridcoScheduleFile(formData);
            // TODO: Show report from GET API (to be added later)
            alert("Report generated successfully!");
        } catch (err) {
            console.error("Error uploading schedule files:", err);
            setError("Error uploading schedule files. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    // ── Simple tab switching (no API call) ──
    const handleTabChange = (tab: UploadTab) => {
        // setActiveUploadTab(tab); // Removed as only DC tab is active
    };

    return (
        <div className="w-full">
            <div className="dashboard-header flex items-center justify-between px-4">
                <Hamburger />
                <div className="ms-5">
                    <TabSwitcher />
                </div>
                <div className="flex-1 flex justify-center">
                    <GridcoTitle />
                </div>
            </div>
            <div className="h-[calc(-73px+100vh)] p-4">
                <div className="w-full h-full flex flex-col gap-4">
                    <div className="gas-chart-container flex flex-col h-full overflow-hidden">
                        <div className="gas-heading">
                            <div className="flex w-full justify-between items-center gap-10">
                                <div className="flex gap-x-4 items-center">
                                    <div className="flex gap-x-1 items-center">
                                        <label className="text-md font-medium text-[#525062] whitespace-nowrap">Date:</label>
                                        <input
                                            type="date"
                                            value={selectedDate}
                                            onChange={(e) => setSelectedDate(e.target.value)}
                                            className="border border-[#C5C5CBD9] rounded-lg px-2 py-1.5 text-sm text-[#525062] focus:outline-none focus:border-[#34656D]"
                                        />
                                    </div>
                                    <UploadTabSwitcher
                                        activeTab={activeUploadTab}
                                        dcCount={dcUploadedCount}
                                        dcTotal={DC_FILE_TYPES.length}
                                    />
                                    <CustomSelect
                                        options={currentFileTypes}
                                        value={currentSelectedFileType}
                                        onChange={(val) => setCurrentSelectedFileType(val)}
                                    />
                                </div>
                                <div className="flex gap-x-4">
                                    <button
                                        type="button"
                                        onClick={handleGenerateReportDC}
                                        disabled={!isDateSelected || !allDcFilesUploaded || loading}
                                        className="flex items-center gap-2 px-6 py-2 text-sm rounded-xl disabled:text-[#A9A8B1] disabled:bg-[#E2E2E5] bg-[#F1935C] text-white font-medium hover:bg-[#d98453] transition-colors"
                                    >
                                        {loading ? "Processing..." : "Generate Report"}
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div className="mx-4 mt-4">
                            <div
                                className="p-4"
                                style={{
                                    backgroundColor: "#F1F5F980",
                                    border: "1px solid #CAD5E2",
                                    borderRadius: "15px",
                                    boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.05)",
                                }}
                            >
                                <div className="flex justify-between items-center">
                                    <div className="max-w-[70%]">
                                        <h3
                                            className="text-xl font-medium mb-1"
                                            style={{ color: "#6F6D7C" }}
                                        >
                                            Add DC Files
                                        </h3>
                                        <p className="text-sm" style={{ color: "#8C8A96" }}>
                                            Attach all the mentioned files below to run the DAM
                                            Performance Analysis. Upload DC (Declared Capacity) files.
                                        </p>
                                    </div>
                                    <button
                                        onClick={handleBulkUploadClick}
                                        disabled={!isDateSelected}
                                        className="flex items-center justify-center gap-2 w-[180px] h-[37px] text-base rounded-lg border-[0.5px] border-[#34656D] font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                        style={{ backgroundColor: "#EFFCFF", color: "#34656D" }}
                                    >
                                        <CloudUpload size={20} /> Bulk Upload
                                    </button>
                                </div>
                                <input
                                    type="file"
                                    ref={scheduleBulkRef}
                                    onChange={handleBulkUpload}
                                    className="hidden"
                                    multiple
                                    accept=".xlsx, .xls, .csv, .pdf"
                                    key="schedule-bulk"
                                />
                                <input
                                    type="file"
                                    ref={dcBulkRef}
                                    onChange={handleBulkUpload}
                                    className="hidden"
                                    multiple
                                    accept=".xlsx, .xls, .csv, .pdf"
                                    key="dc-bulk"
                                />
                            </div>
                            <h2
                                className="py-2 mt-4 chartHeading border-b !text-lg !font-medium"
                                style={{ color: "#6F6D7C" }}
                            >
                                {activeUploadTab} File Types
                            </h2>

                            <div className={`overflow-y-auto relative py-2 mb-20 ${!isDateSelected ? 'pointer-events-none' : ''}`} style={{ height: "calc(100vh - 340px)" }}>
                                {!isDateSelected && (
                                    <div className="absolute inset-0 bg-white/70 z-10 flex items-center justify-center rounded-lg">
                                        <p className="text-lg font-medium text-[#8C8A96]">Please select a date first</p>
                                    </div>
                                )}
                                {filesToDisplay.map((fileName: string) => {
                                    const uploadedFileData = getUploadedFileForStandardType(fileName, currentFiles);
                                    const uploadedFile = uploadedFileData?.file || null;
                                    return (
                                        <FileRow
                                            key={`${activeUploadTab}-${fileName}`}
                                            fileName={fileName}
                                            uploadedFile={uploadedFile}
                                            fileError={currentFileErrors[fileName] || null}
                                            onFileChange={handleFileChange}
                                            onFileError={handleFileError}
                                            onDownloadTemplate={handleDownloadTemplate}
                                        />
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </div>
                <ErrorPopup error={error} onClose={() => setError(null)} />
                <div className="gna-footer-container">
                    <div className="grid grid-cols-2 justify-between gap-5 ">
                        <p className="gna-footer-text col-span-1">
                            Powered by <strong>GNA Energy</strong>
                        </p>
                        <strong className="gna-footer-text col-span-1 text-end">
                            Beta Version
                        </strong>
                    </div>
                </div>
            </div>
        </div>
    );
}
